// layout/Navigation/ChameleonPicker.tsx
// Chameleon controls (superuser + distributor) embedded in the TopNav right side.
// Inactive: entity type dropdown + entity search + Impersonate button.
// Active: compact amber chip showing "eye TVNZ (Broadcaster)" + Exit button.
// No layout shift between states -- both fit within the TopNav height.
import { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Select,
    type SelectChangeEvent,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '@/contexts';
import type { ChameleonEntityType, ChameleonState } from '@/contexts/ChameleonContext';
import { useChameleon } from '@/contexts/ChameleonContext';
import { getAdtechs, getAllAgencies, getAllCustomers, getBroadcasters } from '@/api';
import { type EntityType, entityTypeKey, entityTypeLabel } from '@/types/common';
import { alpha, colors } from '@/styles/base/tokens';

interface EntityOption {
    id: number;
    name: string;
}

// Chameleon supports impersonating these entity types
const CHAMELEON_TYPES: EntityType[] = ['BROADCASTER', 'CUSTOMER', 'AGENCY', 'ADTECH'];

const ENTITY_TYPE_OPTIONS = CHAMELEON_TYPES.map((et) => ({
    value: entityTypeKey(et) as ChameleonEntityType,
    label: et === 'CUSTOMER' ? 'Customer' : entityTypeLabel(et),
}));

const ENTITY_LABELS: Record<string, string> = Object.fromEntries(ENTITY_TYPE_OPTIONS.map((t) => [t.value, t.label]));

const fetchEntities = (entityType: ChameleonEntityType): Promise<EntityOption[]> => {
    switch (entityType) {
        case 'broadcaster':
            return getBroadcasters().then((list) => list.map((e) => ({ id: e.id, name: (e as any).broadcaster_name })));
        case 'customer':
            return getAllCustomers().then((list) => list.map((e) => ({ id: e.id, name: e.cust_name })));
        case 'agency':
            return getAllAgencies().then((list) => list.map((e) => ({ id: e.id, name: (e as any).agency_name })));
        case 'adtech':
            return getAdtechs().then((list) => list.map((e) => ({ id: e.id, name: (e as any).adtech_name })));
        default:
            return Promise.resolve([]);
    }
};

const darkInputSx = {
    '& .MuiInputBase-root': {
        fontSize: '0.72rem',
        color: alpha(colors.appBarText, 0.9),
        height: 28,
        py: 0,
        '.MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.appBarText, 0.25) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.appBarText, 0.5) },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.appBarText, 0.7) },
    },
    '& .MuiInputBase-input': { py: '4px !important' },
    '& .MuiInputBase-input::placeholder': { color: alpha(colors.appBarText, 0.4), opacity: 1 },
};

const navDivider = {
    pl: 2,
    ml: 1,
    borderLeft: `1px solid ${alpha(colors.appBarText, 0.15)}`,
};

// --- Public entry point ---

export function ChameleonPicker() {
    const { authState } = useAuth();
    const { chameleon, deactivate } = useChameleon();

    // Once impersonating, /me intentionally projects the worn entity into authState.
    // The active session must therefore remain sufficient to render its own exit control.
    if (!chameleon.active && !authState.user?.isSuperUser && !authState.user?.isDistributor) return null;

    if (chameleon.active) {
        return <ActiveIndicator chameleon={chameleon} onExit={deactivate} />;
    }
    return <InactivePicker />;
}

// --- Active state: compact amber chip ---

interface ActiveIndicatorProps {
    chameleon: ChameleonState;
    onExit: () => void;
}

function ActiveIndicator({ chameleon, onExit }: ActiveIndicatorProps) {
    const label = ENTITY_LABELS[chameleon.entityType ?? ''] ?? chameleon.entityType;
    return (
        <Box
            data-testid="chameleon-active"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2, ...navDivider }}
        >
            <Tooltip title="Chameleon mode active -- click x to exit" placement="bottom" arrow>
                <Chip
                    icon={<VisibilityIcon sx={{ fontSize: '14px !important' }} />}
                    label={`${chameleon.entityName} (${label})`}
                    onDelete={onExit}
                    deleteIcon={<VisibilityOffIcon data-testid="chameleon-exit" sx={{ fontSize: '14px !important' }} />}
                    size="small"
                    sx={{
                        bgcolor: 'warning.dark',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24,
                        '& .MuiChip-icon': { color: 'white' },
                        '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } },
                    }}
                />
            </Tooltip>
        </Box>
    );
}

// --- Inactive state: entity picker ---

function InactivePicker() {
    const { activate } = useChameleon();

    const [entityType, setEntityType] = useState<ChameleonEntityType>('broadcaster');
    const [entities, setEntities] = useState<EntityOption[]>([]);
    const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setSelectedEntity(null);
        setLoading(true);
        void fetchEntities(entityType)
            .then(setEntities)
            .catch(() => setEntities([]))
            .finally(() => setLoading(false));
    }, [entityType]);

    const handleActivate = () => {
        if (!selectedEntity) return;
        activate(entityType, selectedEntity.id, selectedEntity.name);
    };

    return (
        <Box
            data-testid="chameleon-picker"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2, ...navDivider }}
        >
            <Tooltip title="Chameleon: view app as another entity" placement="bottom" arrow>
                <VisibilityIcon sx={{ fontSize: 14, color: alpha(colors.appBarText, 0.35), flexShrink: 0 }} />
            </Tooltip>

            <Select
                size="small"
                data-testid="chameleon-type-select"
                value={entityType}
                onChange={(e: SelectChangeEvent) => setEntityType(e.target.value as ChameleonEntityType)}
                sx={{
                    fontSize: '0.72rem',
                    color: alpha(colors.appBarText, 0.9),
                    height: 28,
                    minWidth: 108,
                    '.MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.appBarText, 0.25) },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(colors.appBarText, 0.5) },
                    '& .MuiSvgIcon-root': { color: alpha(colors.appBarText, 0.6) },
                    '& .MuiSelect-select': { py: '4px' },
                }}
            >
                {ENTITY_TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t.value} value={t.value} sx={{ fontSize: '0.72rem' }}>
                        {t.label}
                    </MenuItem>
                ))}
            </Select>

            <Autocomplete
                size="small"
                data-testid="chameleon-entity-search"
                options={entities}
                getOptionLabel={(o) => o.name}
                value={selectedEntity}
                onChange={(_, val) => setSelectedEntity(val)}
                loading={loading}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                sx={{ width: 180 }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Search..."
                        sx={darkInputSx}
                        slotProps={{
                            ...params.slotProps,

                            input: {
                                ...params.slotProps.input,
                                endAdornment: (
                                    <>
                                        {loading ? (
                                            <CircularProgress sx={{ color: alpha(colors.appBarText, 0.6) }} size={12} />
                                        ) : null}
                                        {params.slotProps.input.endAdornment}
                                    </>
                                ),
                            },
                        }}
                    />
                )}
            />

            <Button
                size="small"
                variant="outlined"
                data-testid="chameleon-impersonate-btn"
                disabled={!selectedEntity}
                onClick={handleActivate}
                sx={{
                    fontSize: '0.68rem',
                    py: '3px',
                    px: 1,
                    color: alpha(colors.appBarText, 0.8),
                    borderColor: alpha(colors.appBarText, 0.3),
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: alpha(colors.appBarText, 0.7), color: colors.appBarText },
                    '&.Mui-disabled': {
                        color: alpha(colors.appBarText, 0.25),
                        borderColor: alpha(colors.appBarText, 0.1),
                    },
                }}
            >
                Impersonate
            </Button>

            <Typography variant="caption" sx={{ color: alpha(colors.appBarText, 0.25), fontSize: '0.6rem' }}>
                CHAMELEON
            </Typography>
        </Box>
    );
}
